import { useState } from 'react'

import { Box, Icon, Text, Touchable } from '@factorialco/deprecated-design-system'
import { F0Alert } from '@factorialco/f0-react'
import {
  AtsHiringRolesEnum,
  AtsMoveToPhaseModalFragment,
  GetApplicationPhasesDocument,
  MoveToPhaseDocument,
  MoveToPhaseMutation,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCandidateEmailActions } from 'modules/ats/hooks/candidateEmailActions/useCandidateEmailActions'
import { useApplicationPhases } from 'modules/ats/hooks/useApplicationPhases'
import { isHiredStage, sortApplicationPhases } from 'modules/ats/lib/utils/applicationPhase'
import { UNASSIGNED_STAGE, getProperties } from 'modules/ats/lib/utils/hiringStages'

import { MoveToPhaseModalSkeleton } from './Skeleton'

interface Props {
  application: AtsMoveToPhaseModalFragment
  hiringRole: AtsHiringRolesEnum
  onOpenConvertToEmployeeModal: () => void
  onClose: () => void
  isSmartActionsAvailable: boolean
  isOfferLetterEnabled?: boolean
  onMoveToOfferStage?: () => void
}

export const MoveToPhaseModal = ({
  application,
  hiringRole,
  onOpenConvertToEmployeeModal,
  onClose,
  isSmartActionsAvailable,
  isOfferLetterEnabled,
  onMoveToOfferStage,
}: Props) => {
  const jobId = application.jobPosting.id
  const currentApplicationPhaseId = application.applicationPhase.id
  const hasVacancy = !!application.vacancy
  const [selectedPhaseId, setSelectedPhaseId] = useState<string>(currentApplicationPhaseId)

  const { applicationPhases, loading } = useApplicationPhases({
    variables: { atsJobPostingId: jobId },
  })

  const [moveToPhase, { loading: loadingMoveToPhase }] = useMutation(MoveToPhaseDocument, {
    extractorFn: (data: MoveToPhaseMutation) => data.atsMutations.moveToPhaseAtsApplication,
  })

  const { access, company } = useCurrentSession()

  const orderedApplicationPhases = sortApplicationPhases(applicationPhases)

  const hiredPhase = applicationPhases.find((phase) => isHiredStage(phase.hiringStage?.name))
  const candidateHasEmail = !!application.candidate.email

  const { candidateEmailActions } = useCandidateEmailActions({
    atsJobPostingId: jobId,
    enabled: true,
    actionType: 'phase_changed',
  })

  const hasSmartAction = (id: string) => {
    if (!isSmartActionsAvailable) return false

    const phaseChangedSmartAction = candidateEmailActions.flatMap((smartAction) =>
      smartAction.atsApplicationPhases.map((phase) => phase.id)
    )
    return phaseChangedSmartAction.includes(id)
  }

  const phaseOptions = orderedApplicationPhases.map((phase) => ({
    value: phase.id,
    name: phase.name,
    subLabel: candidateHasEmail && hasSmartAction(phase.id),
    color: getProperties(phase.hiringStage?.name ?? UNASSIGNED_STAGE).color,
    selected: phase.id === selectedPhaseId,
  }))

  const onSelectPhase = async (itemValue: string) => {
    setSelectedPhaseId(itemValue)
    if (itemValue === currentApplicationPhaseId || loadingMoveToPhase) {
      return
    }

    const selectedPhase = applicationPhases.find((phase) => phase.id === itemValue)

    const result = await moveToPhase({
      variables: {
        id: application.id,
        authorId: access.id,
        atsApplicationPhaseId: itemValue,
      },
      refetchQueries: [GetApplicationPhasesDocument],
    })

    const handled = handleResult(result, () => null)

    if (handled.success) {
      app.setFlash({
        message: i18n.t('ats.application_details.move_to_phase.success_message'),
        type: 'correct',
      })

      if (selectedPhase) {
        if (hasSmartAction(selectedPhase.id)) {
          track(trackingEvents.ats.smartActionPerform, {
            company_id: company.id,
            stage_id: [selectedPhase.hiringStage?.id],
            stage_name: [selectedPhase.hiringStage?.name || 'Unassigned'],
            job_position: jobId,
            action_type: 'phase_changed',
          })
        }

        track(trackingEvents.ats.candidateMovedPhase, {
          hiring_role: hiringRole,
          new_phase_id: selectedPhase.id,
          old_phase_id: currentApplicationPhaseId,
          user_id: access.id,
        })
      }
    }

    if (itemValue === hiredPhase?.id) {
      setTimeout(() => onOpenConvertToEmployeeModal(), 500)
    }

    const showOfferLetterModal =
      onMoveToOfferStage && isOfferLetterEnabled && selectedPhase?.hiringStage?.name === 'offer'
    if (showOfferLetterModal) {
      onMoveToOfferStage()
      return handled
    }

    onClose()
    return handled
  }

  return (
    <Modal title={i18n.t('ats.application_details.move_to_phase.title')} onClose={onClose}>
      <Box gap="s4">
        {hasVacancy && (
          <Box paddingBottom="s8">
            <F0Alert
              variant="warning"
              title={i18n.t('ats.application_details.move_to_phase.vacancy_warning_title')}
              description={i18n.t(
                'ats.application_details.move_to_phase.vacancy_warning_description'
              )}
            />
          </Box>
        )}
        {loading ? (
          <MoveToPhaseModalSkeleton />
        ) : (
          phaseOptions.map((option) => {
            const descriptionId = `${option.value}-description`

            return (
              <Touchable
                onClick={() => onSelectPhase(option.value)}
                buttonBorderRadius="abs016"
                aria-pressed={option.selected}
                key={option.value}
                disabled={option.selected || loadingMoveToPhase}
              >
                <Box
                  gap="s4"
                  paddingX="s16"
                  paddingY="s12"
                  width="full"
                  borderRadius={{ all: 'abs012' }}
                  background={option.selected ? 'grey100' : undefined}
                >
                  <Box
                    gap="s8"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="spaceBetween"
                  >
                    <Box gap="s8" flexDirection="row" alignItems="center">
                      <Icon.Small icon="circle" color={option.color} />
                      <Text.Normal color="grey800" weight="medium">
                        {option.name}
                      </Text.Normal>
                    </Box>
                    {option.selected && <Icon.Medium icon="check" color="viridian1000" />}
                  </Box>
                  {option.subLabel && (
                    <Box gap="s8" flexDirection="row" alignItems="center">
                      <Icon.Small icon="small-envelope" color="grey600" />
                      <Text.Normal size="200" color="grey800">
                        <span id={descriptionId}>
                          {i18n.t(
                            'ats.application_details.move_to_phase.candidate_will_receive_email'
                          )}
                        </span>
                      </Text.Normal>
                    </Box>
                  )}
                </Box>
              </Touchable>
            )
          })
        )}
      </Box>
    </Modal>
  )
}
